import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Search, Edit, Trash2, X, FilterX, FileText, Loader2,
  LayoutGrid, List, Calendar, Clock
} from "lucide-react";
import { toast } from "sonner";
import pb from "../../lib/pocketbaseClient";

// ---------- Badge ----------
const Badge = ({ children, color = "slate" }) => {
  const colors = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    indigo: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.slate}`}>
      {children}
    </span>
  );
};

// ---------- Categories ----------
const BLOG_CATEGORIES = [
  "Architecture", "Interior Design", "House Plans", "Construction Tips",
  "Vastu", "Home Decoration", "Real Estate", "Civil Engineering",
  "Building Materials", "News"
];

// ---------- Date filter helpers ----------
const getDateRange = (filter) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (filter) {
    case "today":
      return { start: today, end: new Date(today.getTime() + 86400000 - 1) };
    case "week": {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { start, end: now };
    }
    case "month": {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { start, end: now };
    }
    case "year": {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start, end: now };
    }
    default:
      return null;
  }
};

const isInDateRange = (dateStr, filter) => {
  if (!dateStr) return filter === "all";
  const date = new Date(dateStr);
  const range = getDateRange(filter);
  if (!range) return true;
  return date >= range.start && date <= range.end;
};

const BlogPostsManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [dateFilter, setDateFilter] = useState("all"); // all, today, week, month, year
  const [viewMode, setViewMode] = useState("card"); // card, list

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const records = await pb.collection("blogs").getFullList({
        sort: "-created",
        $autoCancel: false,
      });
      setBlogs(records);
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
      toast.error("Could not load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) => {
      const matchesSearch = blog.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === "All" || blog.category === filterCategory;
      const matchesStatus = filterStatus === "All" || blog.status === filterStatus;
      const matchesDate = dateFilter === "all" || isInDateRange(blog.publishDate, dateFilter);
      return matchesSearch && matchesCategory && matchesStatus && matchesDate;
    });
  }, [blogs, searchQuery, filterCategory, filterStatus, dateFilter]);

  const resetFilters = () => {
    setSearchQuery("");
    setFilterCategory("All");
    setFilterStatus("All");
    setDateFilter("all");
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await pb.collection("blogs").delete(deleteConfirm.id);
      setBlogs((prev) => prev.filter((b) => b.id !== deleteConfirm.id));
      toast.success("Blog deleted");
    } catch (err) {
      toast.error("Failed to delete blog");
    } finally {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Blog Posts</h1>
          <p className="text-slate-500 text-sm">Manage your blog articles</p>
        </div>
        <Link
          to="/blogs/new"
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition"
        >
          <Plus className="w-5 h-5" /> New Blog Post
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-start md:items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search blogs..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-44 px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            <option value="All">All Categories</option>
            {BLOG_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-36 px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            <option value="All">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-36 px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">This Year</option>
          </select>
          <button
            onClick={resetFilters}
            className="p-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition"
          >
            <FilterX className="w-5 h-5 text-slate-500" />
          </button>
        </div>
      </div>

      {/* View Toggle & Total Count */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">
          <span className="font-medium text-slate-700">{filteredBlogs.length}</span> posts found
        </div>
        <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode("card")}
            className={`p-2 rounded-lg transition ${viewMode === "card" ? "bg-white shadow-sm text-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-lg transition ${viewMode === "list" ? "bg-white shadow-sm text-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Blog List / Grid */}
      {filteredBlogs.length === 0 ? (
        <div className="text-center py-16 bg-white/60 rounded-2xl border border-slate-200">
          <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No blog posts found</h3>
          <p className="text-slate-500 mb-4">Try adjusting your filters or create a new post.</p>
          <button onClick={resetFilters} className="px-4 py-2 border rounded-xl hover:bg-slate-50">
            Clear Filters
          </button>
        </div>
      ) : (
        <>
          {viewMode === "card" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredBlogs.map((blog) => (
                <motion.div
                  key={blog.id}
                  layout
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
                >
                  {blog.featuredImage && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={pb.files.getUrl(blog, blog.featuredImage)}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-semibold text-slate-800 mb-2 truncate">{blog.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge color="indigo">{blog.category}</Badge>
                      <Badge color={blog.status === "published" ? "green" : "amber"}>
                        {blog.status}
                      </Badge>
                    </div>
                    {blog.publishDate && (
                      <p className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(blog.publishDate).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                    )}
                    <div className="flex justify-end gap-1">
                      <Link
                        to={`/blogs/edit/${blog.id}`}
                        className="p-1.5 hover:bg-slate-100 rounded-lg transition"
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </Link>
                      <button
                        onClick={() => setDeleteConfirm(blog)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            // List View
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Title</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Category</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Publish Date</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredBlogs.map((blog) => (
                      <tr key={blog.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <Link to={`/blogs/edit/${blog.id}`} className="hover:text-indigo-600">
                            {blog.title}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <Badge color="indigo">{blog.category}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge color={blog.status === "published" ? "green" : "amber"}>
                            {blog.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {blog.publishDate ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(blog.publishDate).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          ) : (
                            <span className="text-slate-400">�</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Link
                              to={`/blogs/edit/${blog.id}`}
                              className="p-1.5 hover:bg-slate-100 rounded-lg transition"
                            >
                              <Edit className="w-4 h-4 text-slate-600" />
                            </Link>
                            <button
                              onClick={() => setDeleteConfirm(blog)}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Trash2 className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Delete Blog Post?</h3>
            <p className="text-slate-500 mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border rounded-xl hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BlogPostsManagement;